import React, { useEffect, useMemo, useRef, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import {
  CircularProgress,
  Dialog,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material'
import { getTenantOwners } from '../../apis/admin'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { BadgeOutlined } from '@mui/icons-material'
import UserAvatar from '../UserAvatar'

const MAX_LIST_HEIGHT = 8 * 68

const COPIED_FEEDBACK_MS = 1500

const localMessages = defineMessages({
  title: {
    id: 'screens.siteOwners.title'
  },
  close: {
    id: 'screens.siteOwners.close'
  },
  empty: {
    id: 'screens.siteOwners.empty'
  },
  error: {
    id: 'screens.siteOwners.error'
  },
  copyEmail: {
    id: 'screens.siteOwners.copyEmail'
  },
  copyEmailFor: {
    id: 'screens.siteOwners.copyEmailFor'
  },
  copied: {
    id: 'screens.siteOwners.copied'
  }
})

type SiteOwner = {
  email: string | null
  name: string | null
}

type GetTenantOwnersResponse = {
  data: SiteOwner[] | null
}

type UseSiteOwnersReturnType = {
  owners: SiteOwner[]
  isLoading: boolean
  hasError: boolean
}

const useSiteOwners = (): UseSiteOwnersReturnType => {
  const [owners, setOwners] = useState<SiteOwner[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let isCurrent = true

    getTenantOwners()
      .then(({ data }: GetTenantOwnersResponse) => {
        if (isCurrent) setOwners(data ?? [])
      })
      .catch(error => {
        if (isCurrent) setHasError(true)
        // eslint-disable-next-line no-console
        console.error(error)
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false)
      })

    return () => {
      isCurrent = false
    }
  }, [])

  return {
    owners,
    isLoading,
    hasError
  }
}

const CopyEmailButton = ({ email }: { email: string }) => {
  const intl = useIntl()
  const [hasCopied, setHasCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
      clearTimeout(resetTimer.current)
    }
  }, [])

  const handleCopy = () => {
    if (!navigator.clipboard) return

    navigator.clipboard
      .writeText(email)
      .then(() => {
        if (!isMounted.current) return

        setHasCopied(true)
        clearTimeout(resetTimer.current)
        resetTimer.current = setTimeout(
          () => setHasCopied(false),
          COPIED_FEEDBACK_MS
        )
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error)
      })
  }

  return (
    <Tooltip
      title={intl.formatMessage(
        hasCopied ? localMessages.copied : localMessages.copyEmail
      )}
    >
      <IconButton
        edge="end"
        aria-label={intl.formatMessage(localMessages.copyEmailFor, { email })}
        onClick={handleCopy}
        size="small"
      >
        {hasCopied ? (
          <CheckIcon fontSize="small" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  )
}

const SiteOwnersList = () => {
  const intl = useIntl()
  const { owners, isLoading, hasError } = useSiteOwners()

  const sortedOwners = useMemo(
    () =>
      [...owners].sort((a, b) =>
        (a.name || a.email || '').localeCompare(b.name || b.email || '')
      ),
    [owners]
  )

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    )
  }

  if (hasError) {
    return (
      <Typography sx={{ px: 2 }}>
        {intl.formatMessage(localMessages.error)}
      </Typography>
    )
  }

  if (sortedOwners.length === 0) {
    return (
      <Typography sx={{ px: 2 }}>
        {intl.formatMessage(localMessages.empty)}
      </Typography>
    )
  }

  return (
    <List
      disablePadding
      sx={{
        maxHeight: MAX_LIST_HEIGHT,
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      {sortedOwners.map((owner, index) => (
        <ListItem
          key={owner.email || owner.name || index}
          divider={index < sortedOwners.length - 1}
          secondaryAction={
            owner.email ? <CopyEmailButton email={owner.email} /> : null
          }
        >
          <ListItemAvatar>
            <UserAvatar name={owner.name || owner.email} role="Owner" />
          </ListItemAvatar>
          <ListItemText
            primary={owner.name || owner.email}
            secondary={owner.name ? owner.email : null}
            primaryTypographyProps={{ noWrap: true }}
            secondaryTypographyProps={{ noWrap: true }}
          />
        </ListItem>
      ))}
    </List>
  )
}

export const SiteOwnersDialogButton = () => {
  const intl = useIntl()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [openCount, setOpenCount] = useState(0)

  const handleOpen = () => {
    setOpenCount(count => count + 1)
    setIsDialogOpen(true)
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<BadgeOutlined />}
        color="secondary"
        onClick={handleOpen}
      >
        {intl.formatMessage(localMessages.title)}
      </Button>

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { maxWidth: 400 } }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            padding: '1rem 0'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 1rem'
            }}
          >
            <Typography variant="h1">
              {intl.formatMessage(localMessages.title)}
            </Typography>
            <IconButton
              aria-label={intl.formatMessage(localMessages.close)}
              onClick={() => setIsDialogOpen(false)}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <SiteOwnersList key={openCount} />
        </div>
      </Dialog>
    </>
  )
}
